import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router";
import { Menu, X } from "lucide-react";
import site from "../../content/site.json";

export function SiteNav() {
  const [open, setOpen] = useState(false);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header aria-label="Site header" className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm">
      <nav aria-label="Primary" className="h-20 mx-auto px-[2.5vw] flex items-center justify-between gap-5">
        <Link
          to="/"
          className="font-varsity font-normal text-xl uppercase tracking-tight hover:opacity-60 transition-opacity"
        >
          {site.studioName}
        </Link>

        {/* Desktop link list */}
        <ul className="hidden md:flex gap-5 lg:gap-8 text-sm uppercase">
          {site.navLinks.map((l) => (
            <li key={l.href}>
              <NavLink
                to={l.href}
                end={l.href === "/"}
                className={({ isActive }) =>
                  isActive
                    ? "text-foreground underline underline-offset-8 decoration-foreground/50"
                    : "text-muted-foreground hover:text-foreground transition-colors"
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <a href={site.instagram} target="_blank" rel="noopener noreferrer" className="ml-auto md:ml-0 text-sm hover:underline underline-offset-4">Instagram ↗</a>

        {/* Hamburger toggle — mobile only */}
        <button
          className="md:hidden p-2 -mr-2 text-foreground"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-nav-drawer"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div
          id="mobile-nav-drawer"
          className="md:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-md border-b border-border/40"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
            <p className="font-camera text-xs tracking-[0.2em] uppercase mb-4">{site.studioName}</p>
            <ul className="flex flex-col gap-4 text-xs font-camera tracking-[0.25em] uppercase">
              {site.navLinks.map((l) => (
                <li key={l.href}>
                  <NavLink
                    to={l.href}
                    end={l.href === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      isActive
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground transition-colors"
                    }
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </header>
  );
}
